import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import HoverCard from '../../../components/HoverCard.jsx';
import { useChatDock } from '../../../contexts/ChatDockContext';
import './ChatHeader.css';

const ChatHeader = ({ contact, onBack, onStartAudioCall, onStartVideoCall, onJoinCall, activeCall, groupMembers = [], enrichedData = null, onMarkUnread, onEditGroup, onRemoveGroup, onOpenContactInfo }) => {
  const displayName = contact?.name || contact?.email || 'Chat';
  const linkPath = contact?.type === 'group' ? `/app/groups/${contact?.id}` : `/app/profile/${contact?.id}`;
  const hoverData = enrichedData || { id: contact?.id, display_name: displayName, avatar_url: contact?.avatar_url };
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const chatDock = useChatDock();
  const pinnedContacts = chatDock?.pinnedContacts || [];

  const isPinned = !!(contact && pinnedContacts.some(p => p.contact_id === contact.id && p.contact_type === (contact.type || 'direct')));

  const handleTogglePin = () => {
    if (!contact || !chatDock) return;
    const type = contact.type || 'direct';
    if (isPinned) {
      chatDock.unpinContact?.(contact.id, type);
    } else {
      chatDock.pinContact?.(contact.id, type);
    }
    setShowMenu(false);
  };

  useEffect(() => {
    if (!showMenu) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showMenu]);
  
  return (
    <div className="chat-header">
      <div className="chat-contact">
        {onBack && (
          <button
            type="button"
            className="chat-header-back"
            onClick={onBack}
            aria-label="Back"
            title="Back"
          >
            ←
          </button>
        )}
        {contact ? (
          <>
            {contact.type === 'group' && hoverData.group_banner_url && (
              <img 
                src={hoverData.group_banner_url} 
                alt={displayName} 
                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', marginRight: 12 }} 
              />
            )}
            <HoverCard
              type={contact.type === 'group' ? 'group' : 'profile'}
              context="messenger"
              data={hoverData}
            >
              <button
                type="button"
                className="contact-name-btn"
                onClick={() => onOpenContactInfo?.()}
                title={`View ${displayName}'s info`}
              >
                <h3 style={{ cursor: 'pointer', margin: 0 }} tabIndex={0}>
                  {displayName}
                </h3>
              </button>
            </HoverCard>
          </>
        ) : (
          <h3>Chat</h3>
        )}
        {contact && contact.type === 'group' && groupMembers && groupMembers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
            {groupMembers.slice(0, 5).map((m) => (
              <HoverCard key={m.id} type="profile" context="messenger" data={{ id: m.id, display_name: m.name || m.email, avatar_url: m.avatar_url }}>
                <img
                  src={m.avatar_url || '/avatar-placeholder.png'}
                  alt=""
                  style={{ width: 24, height: 24, borderRadius: '50%' }}
                />
              </HoverCard>
            ))}
            {groupMembers.length > 5 && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>+{groupMembers.length - 5}</span>
            )}
          </div>
        )}
      </div>
      <div className="chat-header-actions">
        {/* Join Call button - shows when there's an active call */}
        {activeCall && (
          <button
            className="call-btn join-call-btn"
            onClick={onJoinCall}
            title="Join ongoing call"
          >
            🔴 Join Call
          </button>
        )}
        
        {/* Call buttons - show for direct chats or groups when no active call */}
        {contact && !activeCall && (
          <>
            <button
              className="call-btn"
              onClick={onStartAudioCall}
              title="Start Audio Call"
              disabled={!contact}
            >
              📞
            </button>
            <button
              className="call-btn"
              onClick={onStartVideoCall}
              title="Start Video Call"
              disabled={!contact}
            >
              🎥
            </button>
          </>
        )}
        {contact && (
          <div className="thread-actions" ref={menuRef}>
            <button className="action-button" title="Thread actions" onClick={() => setShowMenu(v => !v)}>⋯</button>
            {showMenu && (
              <div className="thread-menu">
                <button className="thread-menu-item" onClick={() => { onMarkUnread?.(contact); setShowMenu(false); }}>Mark as unread</button>
                <button className="thread-menu-item" onClick={handleTogglePin}>
                  {isPinned ? 'Unpin conversation' : 'Pin conversation'}
                </button>
                {contact.type === 'group' && (
                  <>
                    <button className="thread-menu-item" onClick={() => { onEditGroup?.(contact); setShowMenu(false); }}>Edit group</button>
                    <button className="thread-menu-item danger" onClick={() => { onRemoveGroup?.(contact.id); setShowMenu(false); }}>Remove group</button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatHeader;